import { store } from '../../state';
import { authService } from '../../services/AuthService';
import { TropicalStormSummary, TropicalDisturbance, TropicalSummaryPayload } from '../../types';

export class TropicalPanel {
  private summaryData: TropicalSummaryPayload | null = null;
  private isFetching: boolean = false;

  constructor() {
    this.fetchSummary();
  }

  public async fetchSummary(): Promise<void> {
    if (this.isFetching) return;
    this.isFetching = true;
    try {
      const state = store.getState();
      const manifest = state.manifest;
      let apiServer = manifest?.api_server;
      if (apiServer === undefined || apiServer === null) {
        apiServer = 'https://auth.skyskout.com';
      } else if (apiServer === '') {
        const path = typeof window !== 'undefined' ? window.location.pathname : '/';
        const dir = path.substring(0, path.lastIndexOf('/') + 1);
        apiServer = (typeof window !== 'undefined' ? window.location.origin : '') + dir;
      }
      if (!apiServer.endsWith('/')) apiServer += '/';
      const tileRoot = (manifest?.tile_base || 'tiles').replace(/^\/+|\/+$/g, '');
      const url = `${apiServer}${tileRoot}/observed/tropical/tropical_summary.json?t=${Date.now()}`;
      
      const token = authService.getSessionToken();
      const headers: Record<string, string> = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      const resp = await fetch(url, {
        credentials: 'include',
        headers
      });
      if (resp.ok) {
        this.summaryData = await resp.json();
      }
    } catch {
      // Ignore network errors on initial render
    } finally {
      this.isFetching = false;
    }
  }

  public render(_state: any): string {
    if (!this.summaryData && !this.isFetching) {
      this.fetchSummary();
    }
    const storms: TropicalStormSummary[] = this.summaryData?.active_storms || [];
    const disturbances: TropicalDisturbance[] = this.summaryData?.disturbances || [];

    const activeStormsSection = storms.length > 0
      ? `
        <div style="display: flex; flex-direction: column; gap: 6px;">
          <div style="font-size: 0.72rem; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.04em; display: flex; justify-content: space-between;">
            <span>Active Systems (${storms.length})</span>
            <span style="color: #ef4444; font-size: 0.68rem; font-weight: 700;">● LIVE NHC</span>
          </div>

          ${storms.map(s => {
            const catBadge = s.category_badge || 'TC';
            const catName = (s as any).category_name || (s.intensity_kt >= 64 ? 'Hurricane' : (s.intensity_kt >= 34 ? 'Tropical Storm' : 'Tropical Depression'));
            const badgeBg = catBadge === 'Cat 5' ? '#581c87' :
                            catBadge === 'Cat 4' ? '#b91c1c' :
                            catBadge === 'Cat 3' ? '#ea580c' :
                            catBadge === 'Cat 2' ? '#f59e0b' :
                            catBadge === 'Cat 1' ? '#eab308' :
                            s.intensity_kt >= 34 ? '#0284c7' : '#06b6d4';
            const badgeText = (catBadge === 'Cat 1' || catBadge === 'Cat 2') ? '#000000' : '#ffffff';
            const windMph = (s as any).intensity_mph || Math.round(s.intensity_kt * 1.15078);
            const hasCoords = typeof s.current_lat === 'number' && typeof s.current_lon === 'number';

            return `
              <div style="display: flex; flex-direction: column; gap: 4px; padding: 7px 9px; background: rgba(15, 23, 42, 0.65); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 6px;">
                <div style="display: flex; align-items: center; justify-content: space-between;">
                  <div style="display: flex; align-items: center; gap: 6px;">
                    <span style="background: ${badgeBg}; color: ${badgeText}; font-size: 0.68rem; font-weight: 800; padding: 1px 6px; border-radius: 4px; border: 1px solid rgba(255,255,255,0.2);">
                      ${catBadge}
                    </span>
                    <strong style="font-size: 0.82rem; color: #f8fafc;">${s.name}</strong>
                  </div>
                  ${hasCoords ? `
                    <button
                      type="button"
                      onclick="window.flyToTropicalStorm(${s.current_lat}, ${s.current_lon})"
                      style="background: rgba(2, 132, 199, 0.2); border: 1px solid rgba(2, 132, 199, 0.4); color: #38bdf8; font-size: 0.68rem; font-weight: 600; padding: 2px 7px; border-radius: 4px; cursor: pointer;"
                      title="Center on map"
                    >
                      Locate 🎯
                    </button>
                  ` : ''}
                </div>

                <div style="font-size: 0.72rem; font-weight: 700; color: ${badgeBg}; margin-top: 1px;">
                  ${catName}
                </div>

                <div style="display: flex; justify-content: space-between; font-size: 0.72rem; color: #cbd5e1; margin-top: 2px;">
                  <span>Winds: <strong style="color: #f1f5f9;">${s.intensity_kt} kt (${windMph} mph)</strong></span>
                  <span>MSLP: <strong style="color: #f1f5f9;">${s.pressure_mb > 0 ? `${s.pressure_mb} mb` : 'N/A'}</strong></span>
                  <span>Move: <strong style="color: #f1f5f9;">${s.movement_speed_kt ?? 0} kt @ ${s.movement_dir ?? 0}°</strong></span>
                </div>

                ${s.advisory_url ? `
                  <div style="display: flex; justify-content: space-between; align-items: center; margin-top: 2px; padding-top: 3px; border-top: 1px solid rgba(255,255,255,0.06); font-size: 0.68rem;">
                    <span style="color: #64748b;">Adv #${s.advisory_num}</span>
                    <a href="${s.advisory_url}" target="_blank" rel="noopener" style="color: #38bdf8; text-decoration: none; font-weight: 600;">
                      Read NHC Advisory ↗
                    </a>
                  </div>
                ` : ''}
              </div>
            `;
          }).join('')}
        </div>
      `
      : `
        <div style="padding: 8px 10px; background: rgba(34, 197, 94, 0.1); border: 1px solid rgba(34, 197, 94, 0.25); border-radius: 6px; font-size: 0.76rem; color: #86efac; display: flex; align-items: center; gap: 6px;">
          <span>✓</span>
          <span>No active named tropical cyclones at this time.</span>
        </div>
      `;

    const disturbancesSection = disturbances.length > 0
      ? `
        <div style="display: flex; flex-direction: column; gap: 6px;">
          <div style="font-size: 0.72rem; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.04em;">
            Formation Outlook (GTWO)
          </div>
          ${disturbances.map(d => {
            const riskBg = d.formation_level === 'High' ? '#ef4444' : (d.formation_level === 'Medium' ? '#f97316' : '#eab308');
            return `
              <div style="display: flex; align-items: center; justify-content: space-between; padding: 5px 8px; background: rgba(15, 23, 42, 0.5); border: 1px solid rgba(255, 255, 255, 0.06); border-radius: 6px;">
                <div style="display: flex; align-items: center; gap: 6px;">
                  <span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background: ${riskBg};"></span>
                  <span style="font-size: 0.75rem; font-weight: 600; color: #f8fafc;">${d.basin} Area ${d.area_id}</span>
                </div>
                <div style="font-size: 0.70rem; color: #cbd5e1;">
                  7-Day: <strong style="color: ${riskBg};">${d.prob_7day}</strong>
                </div>
              </div>
            `;
          }).join('')}
        </div>
      `
      : '';

    const radiiToggleSection = `
      <div style="display: flex; align-items: center; justify-content: space-between; padding: 7px 9px; background: rgba(15, 23, 42, 0.6); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 6px;">
        <div style="display: flex; flex-direction: column;">
          <span style="font-size: 0.72rem; font-weight: 700; color: #f1f5f9;">Future Forecast Radii</span>
          <span style="font-size: 0.64rem; color: #94a3b8;">Show projected wind swaths (+12h to +72h)</span>
        </div>
        <label style="display: inline-flex; align-items: center; cursor: pointer;">
          <input
            type="checkbox"
            id="tropical-forecast-radii-toggle"
            ${(typeof window !== 'undefined' && (window as any).isTropicalForecastRadiiEnabled && (window as any).isTropicalForecastRadiiEnabled()) ? 'checked' : ''}
            onchange="window.toggleTropicalForecastRadii && window.toggleTropicalForecastRadii(this.checked)"
            style="cursor: pointer; accent-color: #0284c7; width: 16px; height: 16px;"
          />
        </label>
      </div>
    `;

    const cartoLegend = `
      <div style="display: flex; flex-direction: column; gap: 8px; border-top: 1px solid rgba(255,255,255,0.08); padding-top: 8px;">
        <!-- 1. Track Point Intensity Scale -->
        <div style="display: flex; flex-direction: column; gap: 4px;">
          <div style="font-size: 0.70rem; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.04em;">
            Track Point Intensity (Saffir-Simpson)
          </div>
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 4px; font-size: 0.68rem; color: #cbd5e1;">
            <div style="display: flex; align-items: center; gap: 6px;">
              <span style="display: inline-flex; align-items: center; justify-content: center; width: 16px; height: 16px; border-radius: 50%; background: #b91c1c; color: #ffffff; font-size: 9px; font-weight: 800; border: 1.5px solid #ffffff; flex-shrink: 0;">4</span>
              <span>Cat 4–5 Major (113+ kt)</span>
            </div>
            <div style="display: flex; align-items: center; gap: 6px;">
              <span style="display: inline-flex; align-items: center; justify-content: center; width: 16px; height: 16px; border-radius: 50%; background: #ea580c; color: #ffffff; font-size: 9px; font-weight: 800; border: 1.5px solid #ffffff; flex-shrink: 0;">3</span>
              <span>Cat 3 Major (96–112 kt)</span>
            </div>
            <div style="display: flex; align-items: center; gap: 6px;">
              <span style="display: inline-flex; align-items: center; justify-content: center; width: 16px; height: 16px; border-radius: 50%; background: #f59e0b; color: #000000; font-size: 9px; font-weight: 800; border: 1.5px solid #ffffff; flex-shrink: 0;">2</span>
              <span>Cat 2 Hurricane (83–95 kt)</span>
            </div>
            <div style="display: flex; align-items: center; gap: 6px;">
              <span style="display: inline-flex; align-items: center; justify-content: center; width: 16px; height: 16px; border-radius: 50%; background: #eab308; color: #000000; font-size: 9px; font-weight: 800; border: 1.5px solid #ffffff; flex-shrink: 0;">1</span>
              <span>Cat 1 Hurricane (64–82 kt)</span>
            </div>
            <div style="display: flex; align-items: center; gap: 6px;">
              <span style="display: inline-flex; align-items: center; justify-content: center; width: 16px; height: 16px; border-radius: 50%; background: #0284c7; color: #ffffff; font-size: 9px; font-weight: 800; border: 1.5px solid #ffffff; flex-shrink: 0;">S</span>
              <span>Tropical Storm (34–63 kt)</span>
            </div>
            <div style="display: flex; align-items: center; gap: 6px;">
              <span style="display: inline-flex; align-items: center; justify-content: center; width: 16px; height: 16px; border-radius: 50%; background: #06b6d4; color: #ffffff; font-size: 9px; font-weight: 800; border: 1.5px solid #ffffff; flex-shrink: 0;">D</span>
              <span>Depression (&lt;34 kt)</span>
            </div>
          </div>
        </div>

        <!-- 2. Current Wind Field Estimate -->
        <div style="display: flex; flex-direction: column; gap: 4px; border-top: 1px solid rgba(255,255,255,0.06); padding-top: 6px;">
          <div style="font-size: 0.70rem; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.04em;">
            Current Wind Field Estimate (Quadrants)
          </div>
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 4px; font-size: 0.68rem; color: #cbd5e1;">
            <div style="display: flex; align-items: center; gap: 6px;">
              <span style="display: inline-block; width: 16px; height: 10px; border-radius: 2px; background: rgba(220, 38, 38, 0.42); border: 1px solid #dc2626; flex-shrink: 0;"></span>
              <span>Hurricane (64+ kt)</span>
            </div>
            <div style="display: flex; align-items: center; gap: 6px;">
              <span style="display: inline-block; width: 16px; height: 10px; border-radius: 2px; background: rgba(234, 88, 12, 0.35); border: 1px solid #ea580c; flex-shrink: 0;"></span>
              <span>Storm Force (50–63 kt)</span>
            </div>
            <div style="display: flex; align-items: center; gap: 6px;">
              <span style="display: inline-block; width: 16px; height: 10px; border-radius: 2px; background: rgba(234, 179, 8, 0.3); border: 1px solid #ca8a04; flex-shrink: 0;"></span>
              <span>Tropical Storm (34–49 kt)</span>
            </div>
          </div>
        </div>

        <!-- 3. Forecast Track & Alerts -->
        <div style="display: flex; flex-direction: column; gap: 4px; border-top: 1px solid rgba(255,255,255,0.06); padding-top: 6px;">
          <div style="font-size: 0.70rem; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.04em;">
            Forecast Track & Coastal Alerts
          </div>
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 4px; font-size: 0.68rem; color: #cbd5e1;">
            <div style="display: flex; align-items: center; gap: 6px;">
              <span style="display: inline-block; width: 14px; height: 6px; border: 1px dashed #e2e8f0; background: rgba(255,255,255,0.25); flex-shrink: 0;"></span>
              <span>5-Day Cone</span>
            </div>
            <div style="display: flex; align-items: center; gap: 6px;">
              <span style="display: inline-block; width: 14px; height: 3px; background: #0f172a; flex-shrink: 0;"></span>
              <span>Track Centerline</span>
            </div>
            <div style="display: flex; align-items: center; gap: 6px;">
              <span style="display: inline-block; width: 14px; height: 3px; background: #ef4444; flex-shrink: 0;"></span>
              <span>Hurricane Warning</span>
            </div>
            <div style="display: flex; align-items: center; gap: 6px;">
              <span style="display: inline-block; width: 14px; height: 3px; background: #f472b6; flex-shrink: 0;"></span>
              <span>Hurricane Watch</span>
            </div>
            <div style="display: flex; align-items: center; gap: 6px;">
              <span style="display: inline-block; width: 14px; height: 3px; background: #3b82f6; flex-shrink: 0;"></span>
              <span>TS Warning</span>
            </div>
            <div style="display: flex; align-items: center; gap: 6px;">
              <span style="display: inline-block; width: 14px; height: 3px; background: #fbbf24; flex-shrink: 0;"></span>
              <span>TS Watch</span>
            </div>
          </div>
        </div>
      </div>
    `;

    return `
      <div style="display: flex; flex-direction: column; gap: 10px; font-family: 'Plus Jakarta Sans', system-ui, sans-serif;">
        ${activeStormsSection}
        ${disturbancesSection}
        ${radiiToggleSection}
        ${cartoLegend}
        <div style="display: flex; align-items: center; justify-content: space-between; font-size: 0.68rem; color: #64748b; margin-top: 2px;">
          <span>Source: NOAA NHC & CPHC</span>
          <span>Click map for details</span>
        </div>
      </div>
    `;
  }
}

export default TropicalPanel;
